import {
  QueryFunctionContext,
  QueryKey,
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from 'react-query';
import { Options } from '@frontend/fetch';
import { ContextlessQueryObserverOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDraftService } from './service';
import { GetIO, ListDraftsCountIO, ListDraftsIO, ListDraftsPageParams } from './types';

const defaultOptions: ContextlessQueryObserverOptions = {
  refetchOnMount: true,
  refetchOnWindowFocus: false,
};

export const queryKeys = {
  baseKey: ['sms-draft'],
  getDraft: (req: GetIO['input']) => [...queryKeys.baseKey, 'get', { ...req }],
  listDrafts: ({ pageToken: _, ...req }: ListDraftsIO['input']) => [...queryKeys.baseKey, 'listDrafts', { ...req }],
  listDraftsCount: (req: ListDraftsCountIO['input']) => [...queryKeys.baseKey, 'listDraftsCount', { ...req }],
};

export const getListDraftsQueryKey = (req: ListDraftsIO['input']) => queryKeys.listDrafts(req);

export type UseGetDraftQueryOptions<E = unknown, T = GetIO['output']> = UseQueryOptions<GetIO['output'], E, T>;
export const useGetDraftQuery = <E = unknown, T = GetIO['output']>(
  req: GetIO['input'],
  options?: UseGetDraftQueryOptions<E, T>,
  httpOptions?: Options
) =>
  useQuery({
    queryKey: queryKeys.getDraft(req),
    queryFn: () => SchemaSMSDraftService.Get(req, httpOptions),
    ...defaultOptions,
    ...options,
  });

export type UseListDraftsQueryOptions<E = unknown, T = ListDraftsIO['output']> = UseQueryOptions<
  ListDraftsIO['output'],
  E,
  T
>;
export const useListDraftsQuery = <E = unknown, T = ListDraftsIO['output']>(
  req: ListDraftsIO['input'],
  options?: UseListDraftsQueryOptions<E, T>
) =>
  useQuery({
    queryKey: queryKeys.listDrafts(req),
    queryFn: () => SchemaSMSDraftService.ListDrafts(req),
    ...defaultOptions,
    ...options,
  });

export type UseInfiniteListDraftsQueryOptions<E = unknown, T = ListDraftsIO['output']> = UseInfiniteQueryOptions<
  ListDraftsIO['output'],
  E,
  T,
  ListDraftsIO['output'],
  QueryKey
>;

export type UseInfiniteListDraftsQueryArgs<E = unknown, T = ListDraftsIO['output']> = {
  request: ListDraftsIO['input'];
  options?: UseInfiniteListDraftsQueryOptions<E, T>;
  httpOptions?: Options;
};

export const useInfiniteListDraftsQuery = <E = unknown, T = ListDraftsIO['output']>({
  request: providedRequest,
  options,
  httpOptions,
}: UseInfiniteListDraftsQueryArgs<E, T>) => {
  const { pageToken: _, ...requestWithoutPageToken } = providedRequest;

  return useInfiniteQuery<ListDraftsIO['output'], E, T, QueryKey>({
    queryKey: queryKeys.listDrafts(requestWithoutPageToken),
    queryFn: ({ pageParam }: QueryFunctionContext<QueryKey, ListDraftsPageParams>) => {
      const requestWithPageParams: ListDraftsIO['input'] = {
        ...providedRequest,
        pageToken: pageParam?.pageToken ?? providedRequest.pageToken,
        pageSize: providedRequest.pageSize ?? 25,
      };
      return SchemaSMSDraftService.ListDrafts(requestWithPageParams, httpOptions);
    },
    ...defaultOptions,
    ...options,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.nextPageToken) {
        return undefined;
      }

      return {
        pageToken: lastPage.nextPageToken,
      };
    },
  });
};

export type UseListDraftsCountsQueryOptions<E = unknown, T = ListDraftsCountIO['output']> = UseQueryOptions<
  ListDraftsCountIO['output'],
  E,
  T
>;
export const useListDraftsCountQuery = <E = unknown, T = ListDraftsCountIO['output']>(
  req: ListDraftsCountIO['input'],
  options?: UseListDraftsCountsQueryOptions<E, T>
) => {
  return useQuery({
    queryKey: queryKeys.listDraftsCount(req),
    queryFn: () => SchemaSMSDraftService.ListDraftsCount(req),
    ...defaultOptions,
    ...options,
    enabled: !!req?.locationId && (options?.enabled ?? true),
  });
};
